import { IconWifi, IconWifiOff } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import classes from './ConnectionBanner.module.css';

/** How long "Back online" stays up before it clears itself. */
export const BACK_ONLINE_MS = 2000;

type State = 'online' | 'offline' | 'back';

/**
 * A small pill at the top of every screen while the browser has no network,
 * so a save that fails offline reads as "you're offline", not as the app
 * being broken. When the network returns it flips to "Back online" for two
 * seconds and clears itself. Being online normally shows nothing.
 *
 * Mounted once at the root (main.tsx), above the router, so it covers sign-in
 * and the public tracking page as well as both portals.
 */
export function ConnectionBanner() {
    const [state, setState] = useState<State>(() =>
        typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online',
    );

    useEffect(() => {
        const goOffline = () => setState('offline');
        // Only a return from offline earns the "Back online" pill; a stray
        // `online` event while already online changes nothing.
        const goOnline = () => setState((prev) => (prev === 'offline' ? 'back' : prev));

        window.addEventListener('offline', goOffline);
        window.addEventListener('online', goOnline);

        return () => {
            window.removeEventListener('offline', goOffline);
            window.removeEventListener('online', goOnline);
        };
    }, []);

    useEffect(() => {
        if (state !== 'back') {
            return;
        }

        const timer = window.setTimeout(() => setState('online'), BACK_ONLINE_MS);

        return () => window.clearTimeout(timer);
    }, [state]);

    if (state === 'online') {
        return null;
    }

    const offline = state === 'offline';

    return (
        <div
            className={classes.banner}
            data-tone={offline ? 'offline' : 'back'}
            role="status"
            aria-live="polite"
        >
            {offline ? <IconWifiOff size={14} stroke={2} /> : <IconWifi size={14} stroke={2} />}
            {offline ? 'No internet connection' : 'Back online'}
        </div>
    );
}
